import { Camera, Sun, User, Sparkles, Grid3x3, Film, Trash2, Palette, Gem, Layers, Download, Lock, ChevronLeft, ChevronRight } from 'lucide-react';
import PanelWrapper from './controls/PanelWrapper';
import { useLanguage } from '@/contexts/LanguageContext';
import ControlField from './controls/ControlField';
import {
  CanvasElement, AppMode, GlobalSettings, FieldConfig,
  CAMERA_FIELDS, LIGHTING_FIELDS, CHARACTER_FIELDS,
  QUALITY_FIELDS, PERSPECTIVE_FIELDS, VFX_FIELDS,
  COLOR_GRADING_FIELDS, TEXTURE_FIELDS, SKIN_BEAUTY_FIELDS, OUTPUT_FIELDS,
} from '@/lib/promptEngine';
import { ScrollArea } from '@/components/ui/scroll-area';
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

interface Props {
  mode: AppMode;
  selectedElement: CanvasElement | null;
  globalSettings: GlobalSettings;
  elements: CanvasElement[];
  onUpdateElement: (id: string, key: string, value: string) => void;
  onUpdateGlobal: (key: string, value: string | number | boolean) => void;
  onRemoveElement: (id: string) => void;
  onEnsureElement: (type: 'camera' | 'light' | 'character') => string;
}

const ADVANCED_EXCLUSIVE_KEYS = new Set([
  'splitToningHighlight',
  'splitToningShadow',
  'fabricTexture',
  'highFrequency',
  'freqSeparation',
  'upscaleMode',
  'exportProfile',
]);

const isFieldVisible = (field: FieldConfig, mode: AppMode) => {
  if (mode === 'advanced') return true;
  if (mode === 'pro') return !ADVANCED_EXCLUSIVE_KEYS.has(field.key);
  return !field.advancedOnly && !field.proOnly;
};

function renderFields(
  fields: FieldConfig[],
  mode: AppMode,
  getValue: (key: string) => string | number | boolean,
  setValue: (key: string, value: string) => void
) {
  return fields
    .filter((f) => isFieldVisible(f, mode))
    .map((f) => (
      <ControlField key={f.key} field={f} value={getValue(f.key)} onChange={(v) => setValue(f.key, v)} />
    ));
}

function renderLockedFields(fields: FieldConfig[], mode: AppMode) {
  if (mode === 'advanced') return null;

  const locked = fields.filter((field) => !isFieldVisible(field, mode)).slice(0, 4);
  if (!locked.length) return null;

  return (
    <div className="space-y-1 mt-2">
      {locked.map((field) => (
        <div key={`locked-${field.key}`} className="flex items-center justify-between rounded border border-border/20 px-2 py-1 text-[9px] font-mono text-muted-foreground/60">
          <span>{field.label}</span>
          <span className="flex items-center gap-1 text-primary/60"><Lock className="w-2.5 h-2.5" /> {mode === 'basic' ? 'PRO' : 'ADV'}</span>
        </div>
      ))}
    </div>
  );
}

const ALL_GLOBAL_FIELDS = [...QUALITY_FIELDS, ...PERSPECTIVE_FIELDS, ...VFX_FIELDS, ...COLOR_GRADING_FIELDS, ...TEXTURE_FIELDS, ...SKIN_BEAUTY_FIELDS, ...OUTPUT_FIELDS];

export default function ControlSidebar({
  mode, selectedElement, globalSettings, elements,
  onUpdateElement, onUpdateGlobal, onRemoveElement, onEnsureElement,
}: Props) {
  const { t } = useLanguage();
  const [collapsed, setCollapsed] = useState(false);
  const elGet = (key: string) => selectedElement?.properties[key] ?? '';
  const elSet = (key: string, value: string) => {
    if (selectedElement) onUpdateElement(selectedElement.id, key, value);
  };

  const gGet = (key: string) => (globalSettings as unknown as Record<string, string | number | boolean>)[key] ?? '';
  const gSet = (key: string, value: string) => {
    const fieldDef = ALL_GLOBAL_FIELDS.find((f) => f.key === key);
    if (fieldDef?.type === 'toggle') onUpdateGlobal(key, value === 'true');
    else if (fieldDef?.type === 'slider') onUpdateGlobal(key, parseInt(value) || 0);
    else onUpdateGlobal(key, value);
  };

  const lightCount = elements.filter((e) => e.type === 'light').length;
  const cameraCount = elements.filter((e) => e.type === 'camera').length;
  const charCount = elements.filter((e) => e.type === 'character').length;

  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (selectedElement && panelRef.current) {
      panelRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [selectedElement?.id]);

  const iconMap = {
    camera: <Camera className="w-3.5 h-3.5" />,
    light: <Sun className="w-3.5 h-3.5" />,
    character: <User className="w-3.5 h-3.5" />,
  };
  const titleMap: Record<string, string> = { camera: t('element.camera'), light: t('element.light'), character: t('element.character') };
  const fieldMap = { camera: CAMERA_FIELDS, light: LIGHTING_FIELDS, character: CHARACTER_FIELDS };
  const badgeMap = { camera: cameraCount, light: lightCount, character: charCount };

  return (
    <div className="relative flex">
      <AnimatePresence initial={false}>
        {!collapsed && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 320, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="inspector-shell rounded-xl flex flex-col min-h-0 border-r border-border/40 bg-gradient-to-b from-card/70 to-background/85 overflow-hidden"
          >
            <div className="px-3 py-3 flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-primary pulse-dot" />
              <span className="text-[9px] font-mono text-primary/70 uppercase tracking-[0.2em]">{t('sidebar.title')}</span>
              <span className="ml-auto text-[8px] font-mono text-primary/60 bg-primary/10 px-1.5 py-0.5 rounded uppercase">{mode}</span>
            </div>
            <div className="h-px bg-gradient-to-r from-transparent via-border/60 to-transparent" />

            <ScrollArea className="flex-1">
              <div ref={panelRef} className="p-2 space-y-1.5">
                {selectedElement && (
                  <motion.div key={selectedElement.id} initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
                    <PanelWrapper title={`${titleMap[selectedElement.type]} — ${selectedElement.label}`} icon={iconMap[selectedElement.type]} badge={badgeMap[selectedElement.type]} defaultOpen forceOpen>
                      {renderFields(fieldMap[selectedElement.type], mode, elGet, elSet)}
                      {renderLockedFields(fieldMap[selectedElement.type], mode)}
                      <RemoveButton onClick={() => onRemoveElement(selectedElement.id)} />
                    </PanelWrapper>
                  </motion.div>
                )}

                {!selectedElement && (
                  <div className="flex flex-col gap-2 py-4 px-2">
                    <p className="text-[10px] text-muted-foreground/60 text-center leading-relaxed">{t('sidebar.noSelection')}</p>
                    <div className="grid grid-cols-3 gap-1.5">
                      <button onClick={() => onEnsureElement('camera')} className="text-[9px] font-mono rounded border border-border/30 py-1.5 hover:border-primary/40 hover:text-primary">{t('element.camera')}</button>
                      <button onClick={() => onEnsureElement('light')} className="text-[9px] font-mono rounded border border-border/30 py-1.5 hover:border-primary/40 hover:text-primary">{t('element.light')}</button>
                      <button onClick={() => onEnsureElement('character')} className="text-[9px] font-mono rounded border border-border/30 py-1.5 hover:border-primary/40 hover:text-primary">{t('element.character')}</button>
                    </div>
                    <p className="text-[10px] text-muted-foreground/50 text-center leading-relaxed whitespace-pre-line">{t('sidebar.clickElement')}</p>
                  </div>
                )}

                <SectionDivider label={t('sidebar.lookGrade')} />

                <PanelWrapper title={t('sidebar.colorGrading')} icon={<Palette className="w-3.5 h-3.5" />}>
                  {renderFields(COLOR_GRADING_FIELDS, mode, gGet, gSet)}
                  {renderLockedFields(COLOR_GRADING_FIELDS, mode)}
                </PanelWrapper>

                <PanelWrapper title={t('sidebar.texture')} icon={<Layers className="w-3.5 h-3.5" />}>
                  {renderFields(TEXTURE_FIELDS, mode, gGet, gSet)}
                  {renderLockedFields(TEXTURE_FIELDS, mode)}
                </PanelWrapper>

                <PanelWrapper title={t('sidebar.skinBeauty')} icon={<Gem className="w-3.5 h-3.5" />}>
                  {renderFields(SKIN_BEAUTY_FIELDS, mode, gGet, gSet)}
                  {renderLockedFields(SKIN_BEAUTY_FIELDS, mode)}
                </PanelWrapper>

                <SectionDivider label={t('sidebar.atmosphere')} />
                <PanelWrapper title={t('sidebar.vfx')} icon={<Sparkles className="w-3.5 h-3.5" />}>
                  {renderFields(VFX_FIELDS, mode, gGet, gSet)}
                  {renderLockedFields(VFX_FIELDS, mode)}
                </PanelWrapper>

                <SectionDivider label={t('sidebar.output')} />
                <PanelWrapper title={t('sidebar.render')} icon={<Film className="w-3.5 h-3.5" />}>
                  {renderFields(QUALITY_FIELDS, mode, gGet, gSet)}
                  {renderLockedFields(QUALITY_FIELDS, mode)}
                </PanelWrapper>

                <PanelWrapper title={t('sidebar.export')} icon={<Download className="w-3.5 h-3.5" />}>
                  {renderFields(OUTPUT_FIELDS, mode, gGet, gSet)}
                  {renderLockedFields(OUTPUT_FIELDS, mode)}
                </PanelWrapper>

                <PanelWrapper title={t('sidebar.composition')} icon={<Grid3x3 className="w-3.5 h-3.5" />}>
                  {renderFields(PERSPECTIVE_FIELDS, mode, gGet, gSet)}
                  {renderLockedFields(PERSPECTIVE_FIELDS, mode)}
                </PanelWrapper>

                <div className="h-4" />
              </div>
            </ScrollArea>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Collapse/Expand toggle arrow */}
      <button
        onClick={() => setCollapsed(prev => !prev)}
        className="absolute top-1/2 -translate-y-1/2 -right-3 z-30 w-6 h-12 rounded-r-lg border border-l-0 border-border/40 bg-card/90 backdrop-blur-sm flex items-center justify-center hover:bg-primary/10 hover:border-primary/30 transition-all shadow-sm"
        title={collapsed ? 'Show sidebar' : 'Hide sidebar'}
      >
        {collapsed ? <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/70" /> : <ChevronLeft className="w-3.5 h-3.5 text-muted-foreground/70" />}
      </button>
    </div>
  );
}

function SectionDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 py-2">
      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-primary/15 to-transparent" />
      <span className="text-[7px] font-mono text-primary/40 uppercase tracking-[0.3em]">{label}</span>
      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-primary/15 to-transparent" />
    </div>
  );
}

function RemoveButton({ onClick }: { onClick: () => void }) {
  const { t } = useLanguage();
  return (
    <button onClick={onClick} className="flex items-center gap-1.5 text-[9px] font-mono text-destructive/60 hover:text-destructive transition-colors pt-2 mt-1 border-t border-border/20">
      <Trash2 className="w-3 h-3" />
      {t('sidebar.removeElement')}
    </button>
  );
}
